/**
 * Onboarding after sign-in: authorize Amazon, choose Shops, watch the first sync.
 *
 * Both entry points render this — the login page continues straight into it,
 * and `/onboarding` is where a seller lands when they come back to finish.
 */
import { IconLoader } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  type ActivatedShops,
  AmazonConnectFlow,
  StepHeader,
} from "@/components/onboarding/amazon-connect-flow";
import { SyncProgress } from "@/components/onboarding/sync-progress";
import { fetchAuthUser, hydrateAuthUser } from "@/lib/auth-api";
import { useAuthStore } from "@/stores/auth";

async function selectFirstShop(data: ActivatedShops): Promise<void> {
  // The seller had no Shops before this step, so the store has no current Shop and the
  // shop-scoped sync status below would never load. A failed refresh is not fatal: the
  // route guard rehydrates on the next navigation, and the first Shop is selected anyway.
  await fetchAuthUser().then(hydrateAuthUser, () => undefined);
  const first = data.shops[0];
  if (first) useAuthStore.getState().setCurrentShop(first.id);
}

export function OnboardingFlow() {
  const { t } = useTranslation("auth");

  return (
    <AmazonConnectFlow
      onActivated={selectFirstShop}
      renderFinish={() => (
        <>
          <StepHeader
            icon={<IconLoader className="size-5" />}
            title={t("syncing.title")}
            description={t("syncing.description")}
          />
          <SyncProgress />
        </>
      )}
    />
  );
}
